<script lang="ts" setup>
const { $directus, $readItems } = useNuxtApp()

const currentPage = ref(1)
const perPage = 6

const { t } = useI18n()

const colors = [
  {
    key: 'inspiring_white',
    name: 'Inspiring White',
  },
  {
    key: 'soothing_blue',
    name: 'Soothing Blue',
  },
  // {
  //   key: 'relaxing_blue',
  //   name: 'Relaxing Blue',
  // },
  {
    key: 'elegant_grey',
    name: 'Elegant Grey',
  },
  {
    key: 'stunning_grey',
    name: 'Stunning Grey',
  },
  // {
  //   key: 'amazing_graphite',
  //   name: 'Amazing Graphite',
  // },
]

const poolTypes = [
  {
    key: 'nova_line',
    name: 'Nova Line',
  },
  {
    key: 'infinity_line',
    name: 'Infinity Line',
  },
  {
    key: 'dynamic_line',
    name: 'Dynamic Line',
  },
  {
    key: 'urban_active',
    name: 'Urban Active',
  },
  {
    key: 'city_line',
    name: 'City Line',
  },
  {
    key: 'essential_line',
    name: 'Essential Line',
  },
  {
    key: 'classic_line',
    name: 'Classic Line',
  },
]

const stairsTypes = computed(() => {
  return [
    {
      key: 'wide_lounge',
      name: t('project.stairs.wide_lounge'),
    },
    {
      key: 'stair_left_lounge',
      name: t('project.stairs.stair_left_lounge'),
    },
    {
      key: 'stair_right_lounge',
      name: t('project.stairs.stair_right_lounge'),
    },
    {
      key: 'stair_both_lounge',
      name: t('project.stairs.stair_both_lounge'),
    },
    {
      key: 'stair_oblique',
      name: t('project.stairs.stair_oblique'),
    },
    {
      key: 'stair_lounge',
      name: t('project.stairs.stair_lounge'),
    },
    {
      key: 'stair_classic',
      name: t('project.stairs.stair_classic'),
    },
    {
      key: 'stair_essential',
      name: t('project.stairs.stair_essential'),
    },
    {
      key: 'stair_left',
      name: t('project.stairs.stair_left'),
    },
    {
      key: 'stair_right',
      name: t('project.stairs.stair_right'),
    },
  ]
})

const selectedPoolType = ref<string | undefined>(undefined)
const selectedColor = ref<string | undefined>(undefined)
const selectedStairsType = ref<string | undefined>(undefined)

const filters = computed(() => {
  const filters: any = [{
    status: {
      _eq: 'published',
    },
  }]

  if (selectedPoolType.value) {
    filters.push({
      pool_type: {
        _eq: selectedPoolType.value,
      },
    })
  }

  if (selectedColor.value) {
    filters.push({
      color: {
        _eq: selectedColor.value,
      },
    })
  }

  if (selectedStairsType.value) {
    filters.push({
      stair_type: {
        _eq: selectedStairsType.value,
      },
    })
  }
  return {
    _and: filters,
  }
})

interface ProjectData {
  count: number
  page: number
  data: {
    id: string
    slug: string
    teaser_image: string
    pool_type: string
    color: string
    stair_type: string
  }[]
}

const { data, pending } = await useAsyncData<ProjectData>(
  'project_index',
  async () => {
    const page = currentPage.value
    const dataPromise = $directus.request($readItems('projects', {
      page,
      limit: perPage,
      sort: '-date_created',
      filter: filters.value,
      fields: [
        'id',
        'slug',
        'teaser_image',
        'pool_type',
        'color',
        'stair_type',
        {
          listing_translations: ['*'],
        },
      ],
    }))
    const countPromise = $directus.request($readItems('projects', {
      filter: filters.value,
      aggregate: {
        count: '*',
      },
    }))
    const [data, count] = await Promise.all([dataPromise, countPromise])
    return {
      data,
      count: Number.parseInt(count[0].count),
      page,
    } as any
  },
  {
    watch: [currentPage, filters],
  },
)

const results = useState<ProjectData['data']>('projectsIndexResults', () => data.value?.data ?? [])

watch(data, (value) => {
  if (!value?.data)
    return
  if (value.page === 1) {
    results.value = value.data
    return
  }
  results.value = [...results.value, ...value.data]
})

watch(filters, () => {
  currentPage.value = 1
})
</script>

<template>
  <BlockBase>
    <Container class="mb-6rem mt-8rem">
      <h2 class="pb-2rem text-center text-balance text-2.5rem tracking-px font-title">
        {{ t('project.get_inspired') }}
      </h2>
    </Container>
    <div class="relative">
      <!-- Border -->
      <Container class="absolute inset-0 border border-neutral-darkGray" />
      <!-- Gradient over border -->
      <div class="bg-gradient absolute inset-x-0 top-0 h-350px" />
      <!-- White background -->
      <BlockBase is-light class="absolute inset-x-0 bottom-0 top-350px bg-neutral-white" />
      <Container class="absolute relative inset-x-0 grid grid-cols-1 gap-x-2rem gap-y-2rem px-6rem -top-4.5rem md:grid-cols-3 xl:gap-x-4rem">
        <label class="flex flex-col gap-.1rem text-1.4rem">
          {{ t('project.label_pool_types') }}
          <FilterSelect v-model="selectedPoolType" :items="poolTypes" class="w-full" :placeholder="t('project.filter_all')" />
        </label>
        <label class="flex flex-col gap-.1rem text-1.4rem">
          {{ t('project.label_colors') }}
          <FilterSelect v-model="selectedColor" :items="colors" class="w-full" :placeholder="t('project.filter_all')" />
        </label>
        <label class="flex flex-col gap-.1rem text-1.4rem">
          {{ t('project.label_stairs') }}
          <FilterSelect v-model="selectedStairsType" :items="stairsTypes" class="w-full" :placeholder="t('project.filter_all')" />
        </label>
      </Container>
      <Container class="relative min-h-350px px-6rem pb-12rem pt-2rem">
        <div class="pointer-events-none absolute inset-x-0 grid place-items-center -top-3rem">
          <AppLoadingIndicator class="size-4rem transition" :class="pending ? 'opacity-100' : 'opacity-0'" />
        </div>

        <div v-auto-animate class="grid grid-cols-1 gap-4rem lg:grid-cols-2 xl:grid-cols-3">
          <div v-if="!results.length && !pending" class="pb-6rem pt-4rem text-center text-1.6rem lg:col-span-2 xl:col-span-3">
            {{ t('project.index_empty') }}
          </div>
          <template v-for="project in results" :key="project.id">
            <NuxtLinkLocale
              :to="`/projects/${project.slug}`"
              class="group relative bg-white text-neutral-black"
            >
              <div class="relative overflow-hidden">
                <NuxtImg
                  loading="lazy"
                  provider="directus" :src="project.teaser_image"
                  height="400" width="500"
                  load="lazy"
                  class="block h-400px w-full object-cover transition-300 group-hover:scale-105"
                />
                <!-- <NuxtImg
                  provider="directus"
                  height="400"
                  :src="project.teaser_image"
                  class="h-400px w-full object-cover transition-300 group-hover:scale-105"
                /> -->
              </div>
              <div class="absolute left-0 top-0 w-full flex">
                <div class="grid h-6rem items-center bg-neutral-white px-2rem text-neutral-gray group-hover:text-neutral-black">
                  <h2 class="whitespace-nowrap text-1.6rem font-title capitalize transition-all">
                    {{ project.pool_type.replace(/_/g, ' ') }}
                  </h2>
                  <h3 class="whitespace-nowrap tracking-3px uppercase">
                    {{ project.color.replace(/_/g, ' ') }}
                  </h3>
                </div>
                <div class="grid grid-cols-[0fr] w-full transition-all group-hover:grid-cols-[1fr]">
                  <div class="grid grid-cols-[1fr_min-content] min-w-0 items-center gap-2rem overflow-hidden bg-white">
                    <div class="h-px w-full bg-black" />
                    <IconArrowRight class="mr-2rem size-2.5rem" />
                  </div>
                </div>
              </div>
            </NuxtLinkLocale>
          </template>
          <div v-if="results.length < (data?.count ?? Infinity)" class="mt-8rem w-full flex flex-col gap-2rem lg:col-span-2 xl:col-span-3">
            <div class="text-center text-1.2rem text-neutral-darkGray">
              {{ t('project.index_counter', { number: results.length, total: data?.count }) }}
            </div>
            <button class="mx-auto btnInvert" @click="currentPage++">
              {{ t('project.load_more') }}
            </button>
          </div>
        </div>
      </Container>
    </div>
  </BlockBase>
</template>

<style scoped>
.bg-gradient {
  background-image: linear-gradient(to bottom, transparent 0%, #000b1a 50%);
}
</style>
